import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Select, Col, Row,Button,Drawer, Table, Modal, message, Dropdown} from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import {formatTimeRangeToISO} from "../../../../utils/dateTimeUtils";
import { ChannelSelectFilter } from '../../../../components/settlement/ChannelSelectFilter';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import CreateSchemeComp from './CreateSchemeComp';
import { QUERY_PRICING_SCHEMES, type PricingSchemeItem, type PricingSchemeQueryResponse } from 'graphql/queries/settlement/getPricingSchemeList';
import { DELETE_PRICING_SCHEME, type DeletePricingSchemeResponse } from 'graphql/mutations/settlement/deletePricingScheme';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';
import { formatFixedUtcDateTime } from 'utils/timezoneFormatter';
import { useMutation } from '@apollo/client';
import {useHashState} from '../../../../utils/hashState';
import { useTranslation } from 'react-i18next';
import ParcelsDownloadModal from '../../parcels/components/ParcelsDownloadModal';
import {
    CREATE_PRICING_SCHEME_DOWNLOAD_TASK,
    CreatePricingSchemeDownloadTaskResponse,
    CreatePricingSchemeDownloadTaskVariables
} from '../../../../graphql/mutations/settlement/createPricingSchemeDownloadTask';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';



interface PricingSchemeTabProps {
    businessScenario: number | null;
}

const PricingSchemeTab: React.FC<PricingSchemeTabProps> = ({ businessScenario }) => {
    const { t } = useTranslation();

    // 从 URL hash 中读取 clientId
    const [hashState] = useHashState({});
    const urlClientId = hashState.clientId as string | undefined;
    const hasProcessedUrlClientId = useRef(false);

    const [open, setOpen] = useState(false);
    const [drawerKey, setDrawerKey] = useState(0);
    const [viewMode, setViewMode] = useState<'create' | 'view' | 'edit'>('create');
    const [viewingSchemeId, setViewingSchemeId] = useState<number | null>(null);
    /** 复制时预填的方案 ID，仅在与 viewMode='create' 同时使用时有效 */
    const [copyFromSchemeId, setCopyFromSchemeId] = useState<number | null>(null);
    
    // 筛选条件状态
    const [clientId, setClientId] = useState<string | undefined>(urlClientId);
    const [createdAtRange, setCreatedAtRange] = useState<[string | null, string | null] | null>(null);
    const [creatorName, setCreatorName] = useState<string | undefined>(undefined);
    const [pricingSchemeId, setPricingSchemeId] = useState<string | undefined>(undefined);
    
    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    
    // 实际使用的筛选条件（点击 Filter 按钮后更新）
    const [activeFilters, setActiveFilters] = useState({
        clientId: urlClientId as string | undefined,
        createdAt: null as [string | null, string | null] | null,
        creatorName: undefined as string | undefined,
        pricingSchemeId: undefined as string | undefined,
    });

    const showDrawer = () => {
        // 清除缓存，不保留上次的编辑记录
        setViewMode('create');
        setViewingSchemeId(null);
        setCopyFromSchemeId(null);
        setDrawerKey(prev => prev + 1);
        setOpen(true);
    };

    // 复制方案：打开 New Plan 并预填被复制方案的数据
    const showSchemeCopy = (schemeId: number) => {
        setViewMode('create');
        setViewingSchemeId(null);
        setCopyFromSchemeId(schemeId);
        setDrawerKey(prev => prev + 1);
        setOpen(true);
    };

    // 查看方案详情
    const showSchemeDetail = (schemeId: number) => {
        setViewMode('view');
        setViewingSchemeId(schemeId);
        setDrawerKey(prev => prev + 1);
        setOpen(true);
    };

    // 编辑方案
    const showSchemeEdit = (schemeId: number) => {
        setViewMode('edit');
        setViewingSchemeId(schemeId);
        setCopyFromSchemeId(null);
        setDrawerKey(prev => prev + 1);
        setOpen(true);
    };

    // 删除方案
    const [deletePricingScheme, { loading: deleting }] = useMutation<DeletePricingSchemeResponse>(
        DELETE_PRICING_SCHEME,
        {
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        }
    );

    const handleDelete = (schemeId: number, schemeName: string) => {
        // 第一次确认
        Modal.confirm({
            title: <span style={{ color: '#ff4d4f' }}>确认删除</span>,
            content: `确定要删除定价方案"${schemeName}"吗？此操作将逻辑删除该方案及其所有价格配置！！！`,
            okText: '确认',
            cancelText: '取消',
            okType: 'danger',
            width: 520,
            onOk: () => {
                // 第二次确认
                Modal.confirm({
                    title: <span style={{ color: '#ff4d4f' }}>再次确认删除</span>,
                    content: '真的要删除吗？删除后就不能用了呦～～～',
                    okText: '确认删除',
                    cancelText: '取消',
                    okType: 'danger',
                    width: 520,
                    onOk: async () => {
                        try {
                            const { data, errors } = await deletePricingScheme({
                                variables: { pricingSchemeId: schemeId },
                                fetchPolicy: 'network-only'
                            });

                            if (errors && errors.length > 0) {
                                message.error(errors[0].message || '删除失败');
                                return;
                            }

                            const res = data?.deletePricingScheme;
                            if (res?.success) {
                                message.success(t('settlement.priceCenter.messages.deleteSuccess'));
                                // 删除成功后刷新表格
                                handleFilter();
                            } else {
                                message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.deleteFailed')));
                            }
                        } catch (e: any) {
                            message.error(e?.message || t('errors.INTERNAL_ERROR'));
                        }
                    }
                });
            }
        });
    };

    const onClose = () => {
        setOpen(false);
        setCopyFromSchemeId(null);
    };

    // 保存成功后刷新表格
    const handleSaveSuccess = () => {
        setOpen(false);
        // 执行一次 Filter 操作以刷新表格
        handleFilter();
    };

    // 构建查询变量
    const queryVariables = useMemo(() => {
        const variables: any = {
            page: currentPage,
            size: pageSize
        };
        
        if (businessScenario != null) {
            variables.businessScenario = businessScenario;
        }
        
        if (activeFilters.clientId) {
            // ChannelSelectFilter 返回的是字符串类型的 channel id，需要转换为 number
            variables.clientId = parseInt(activeFilters.clientId, 10);
        }
        if (activeFilters.pricingSchemeId) {
            variables.pricingSchemeId = parseInt(activeFilters.pricingSchemeId, 10);
        }
        if (activeFilters.creatorName) {
            variables.creatorName = activeFilters.creatorName;
        }
        if (activeFilters.createdAt && activeFilters.createdAt[0] && activeFilters.createdAt[1]) {
            const timeRange = formatTimeRangeToISO(activeFilters.createdAt);
            if (timeRange && timeRange.start && timeRange.end) {
                variables.createdAtStart = timeRange.start;
                variables.createdAtEnd = timeRange.end;
            }
        }
        
        return variables;
    }, [activeFilters, businessScenario, currentPage, pageSize]);

    // 查询定价方案列表
    const { data: schemeData, loading: schemeLoading, refetch } = useQuery<PricingSchemeQueryResponse>(
        QUERY_PRICING_SCHEMES,
        {
            variables: queryVariables,
            skip: !businessScenario,
            fetchPolicy: 'network-only'
        }
    );

    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [createPricingSchemeDownloadTask, { loading: exportLoading }] = useMutation<
        CreatePricingSchemeDownloadTaskResponse,
        CreatePricingSchemeDownloadTaskVariables
    >(CREATE_PRICING_SCHEME_DOWNLOAD_TASK);

    // 当从 URL hash 中读取到 clientId 时，自动设置筛选条件
    useEffect(() => {
        if (urlClientId && !hasProcessedUrlClientId.current) {
            setClientId(urlClientId);
            setActiveFilters(prev => ({
                ...prev,
                clientId: urlClientId
            }));
            setCurrentPage(1);
            hasProcessedUrlClientId.current = true;
        }
    }, [urlClientId]);

    // 当 businessScenario 加载完成且 URL 中有 clientId 时，自动触发查询
    useEffect(() => {
        if (urlClientId && businessScenario && hasProcessedUrlClientId.current) {
            const variables: any = {
                page: 1,
                size: pageSize,
                businessScenario: businessScenario,
                clientId: parseInt(urlClientId, 10)
            };
            refetch(variables);
            // 标记为已处理，避免重复查询
            hasProcessedUrlClientId.current = false;
        }
    }, [urlClientId, businessScenario, refetch, pageSize]);

    // 处理筛选
    const handleFilter = () => {
        const newFilters = {
            clientId,
            createdAt: createdAtRange,
            creatorName,
            pricingSchemeId
        };
        setActiveFilters(newFilters);
        setCurrentPage(1);
        
        // 构建新的查询变量并立即查询
        const variables: any = {
            page: 1,
            size: pageSize
        };
        
        if (businessScenario != null) {
            variables.businessScenario = businessScenario;
        }
        if (clientId) {
            variables.clientId = parseInt(clientId, 10);
        }
        if (pricingSchemeId) {
            variables.pricingSchemeId = parseInt(pricingSchemeId, 10);
        }
        if (creatorName) {
            variables.creatorName = creatorName;
        }
        if (createdAtRange && createdAtRange[0] && createdAtRange[1]) {
            const timeRange = formatTimeRangeToISO(createdAtRange);
            if (timeRange && timeRange.start && timeRange.end) {
                variables.createdAtStart = timeRange.start;
                variables.createdAtEnd = timeRange.end;
            }
        }
        
        refetch(variables);
    };

    // 处理重置
    const handleReset = () => {
        setClientId(undefined);
        setCreatedAtRange(null);
        setCreatorName(undefined);
        setPricingSchemeId(undefined);
        setActiveFilters({
            clientId: undefined,
            createdAt: null,
            creatorName: undefined,
            pricingSchemeId: undefined
        });
        setCurrentPage(1);
        
        // 重置后立即查询
        const resetQueryVariables: any = {
            page: 1,
            size: pageSize
        };
        
        if (businessScenario != null) {
            resetQueryVariables.businessScenario = businessScenario;
        }
        
        refetch(resetQueryVariables);
    };

    // 处理分页变化
    const handleTableChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    // 导出功能处理函数
    const handleExport = async (type: 'csv' | 'excel' | 'json') => {
        // 获取总记录数
        const totalElements = schemeData?.queryPricingSchemes?.totalElements || 0;
        if (totalElements === 0) {
            message.warning('没有数据可导出');
            return;
        }
        try {
            const exportVariables: CreatePricingSchemeDownloadTaskVariables = {
                format: type
            };

            if (businessScenario != null) {
                exportVariables.businessScenario = businessScenario;
            }
            if (activeFilters.clientId) {
                exportVariables.clientId = parseInt(activeFilters.clientId, 10);
            }
            if (activeFilters.pricingSchemeId) {
                exportVariables.pricingSchemeId = parseInt(activeFilters.pricingSchemeId, 10);
            }
            if (activeFilters.creatorName) {
                exportVariables.creatorName = activeFilters.creatorName;
            }
            if (activeFilters.createdAt && activeFilters.createdAt[0] && activeFilters.createdAt[1]) {
                const timeRange = formatTimeRangeToISO(activeFilters.createdAt);
                if (timeRange && timeRange.start && timeRange.end) {
                    exportVariables.createdAtStart = timeRange.start;
                    exportVariables.createdAtEnd = timeRange.end;
                }
            }

            const { data } = await createPricingSchemeDownloadTask({
                variables: exportVariables,
                fetchPolicy: 'network-only'
            });
            const exportResult = data?.createPricingSchemeDownloadTask;
            if (!exportResult?.data) {
                message.error(resolvePricingSchemeMessage(exportResult, '提交下载中心失败，请重试'));
                return;
            }
            setGeneratedFileName(exportResult.data);
            setDownloadModalVisible(true);
        } catch (error) {
            console.error('导出失败:', error);
            message.error('提交下载中心失败，请重试');
        }
    };

    // 导出菜单项
    const exportMenuItems: MenuProps['items'] = [
        {
            key: 'csv',
            label: '导出为CSV',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('csv'),
        },
        {
            key: 'excel',
            label: '导出为Excel',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('excel'),
        },
        {
            key: 'json',
            label: '导出为JSON',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('json'),
        },
    ];

    // 表格列定义
    const columns: ColumnsType<PricingSchemeItem> = [
        {
            title: '方案ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? '-'}</div>,
        },
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? '-'}</div>,
        },
        {
            title: '方案名称',
            dataIndex: 'pricingSchemeName',
            key: 'pricingSchemeName',
            width: 200,
            ellipsis: false,
            render: (text, record) => {
                if (!text || !record.id) {
                    return (
                        <div style={{ 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            maxWidth: '200px'
                        }}>
                            {text ?? '-'}
                        </div>
                    );
                }
                return (
                    <Button 
                        type="link" 
                        style={{ 
                            padding: 0, 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word',
                            textAlign: 'left',
                            height: 'auto',
                            overflow: 'hidden',
                            maxWidth: '200px',
                            display: 'block'
                        }}
                        onClick={() => showSchemeDetail(record.id!)}
                    >
                        {text}
                    </Button>
                );
            },
        },
        {
            title: '创建时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatFixedUtcDateTime(text)}</div>,
        },
        {
            title: '更新时间',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatFixedUtcDateTime(text)}</div>,
        },
        {
            title: '绑定数量',
            dataIndex: 'bindCount',
            key: 'bindCount',
            width: 100,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? '-'}</div>,
        },
        {
            title: '配置人',
            dataIndex: 'creator',
            key: 'creator',
            width: 120,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? '-'}</div>,
        },
        {
            title: '操作人',
            dataIndex: 'operator',
            key: 'operator',
            width: 120,
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? '-'}</div>,
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            fixed: 'right',
            ellipsis: false,
            render: (_, record) => (
                <>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => showSchemeCopy(record.id!)}
                            disabled={!record.id}
                        >
                            复制
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => showSchemeEdit(record.id!)}
                            disabled={!record.id}
                        >
                            编辑
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                        <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => handleDelete(record.id!, record.pricingSchemeName)}
                            disabled={!record.id || deleting}
                            loading={deleting}
                        >
                            删除
                        </Button>
                    </HasResource>
                </>
            ),
        },
    ];

    return (
        <div className="mb-6">
            <Row gutter={[16, 18]} wrap>
                <Col xs={24} sm={12} md={12} lg={6} xl={6} >
                    <ChannelSelectFilter
                        label="客户名称"
                        value={clientId}
                        onChange={(value) => setClientId(value)}
                        spanClassName="w-[9em]"
                    />
                </Col>
                <Col xs={24} sm={24} md={24} lg={12} xl={10} >
                    <DateRangeFilterItem
                        label="方案创建时间"
                        spanClassName="w-[9em]"
                        value={createdAtRange}
                        onChange={(range) => {
                            setCreatedAtRange(range);
                        }}
                    />
                </Col>
                <Col xs={24} sm={12} md={12} lg={6} xl={6} >
                    <InputFilterItem
                        label="创建人"
                        spanClassName="w-[9em]"
                        value={creatorName}
                        onChange={(e) => setCreatorName(e.target.value || undefined)}
                    />
                </Col>
                <Col xs={24} sm={12} md={12} lg={6} xl={6} >
                    <InputFilterItem
                        label="方案ID"
                        spanClassName="w-[9em]"
                        value={pricingSchemeId}
                        onChange={(e) => setPricingSchemeId(e.target.value || undefined)}
                    />
                </Col>
                <Col span={24} >
                    <Row justify="end">
                        <Col>
                            <div className="flex gap-4">
                                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                                    <Button
                                        type="primary"
                                        onClick={showDrawer}
                                        disabled={!businessScenario}
                                        title={!businessScenario ? '请先选择业务场景' : ''}
                                    >
                                        New Plan
                                    </Button>
                                </HasResource>

                                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_CHECK]}>
                                    <Button
                                        type="primary"
                                        onClick={handleFilter}
                                    >
                                        Filter
                                    </Button>
                                </HasResource>

                                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_OPERATE]}>
                                    <Dropdown
                                        menu={{ items: exportMenuItems }}
                                        placement="bottomRight"
                                        trigger={['click']}
                                    >
                                        <Button
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            loading={exportLoading}
                                        >
                                            Export <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                </HasResource>
                                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_CHECK]}>
                                    <Button
                                        type="default"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </Button>
                                </HasResource>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* 表格 */}
            <div className="mt-6">
                <Table
                    columns={columns}
                    dataSource={schemeData?.queryPricingSchemes?.items || []}
                    loading={schemeLoading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: schemeData?.queryPricingSchemes?.totalElements || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                        onChange: handleTableChange,
                        onShowSizeChange: handleTableChange,
                    }}
                />
            </div>

            <Drawer
                title={
                    viewMode === 'create' ? "新建定价方案/New Plan" : 
                    viewMode === 'edit' ? "编辑定价方案/Edit Plan" : 
                    "查看定价方案/View Plan"
                }
                width={800}
                onClose={onClose}
                open={open}
                maskClosable={false}
                styles={{
                body: {
                    paddingBottom: 80,
                },
                }}
            >
                <CreateSchemeComp 
                    key={drawerKey}
                    businessScenario={businessScenario} 
                    onSaveSuccess={handleSaveSuccess}
                    viewMode={viewMode}
                    pricingSchemeId={viewingSchemeId}
                    copyFromSchemeId={viewMode === 'create' ? copyFromSchemeId : null}
                />
            </Drawer>
            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
}

export default PricingSchemeTab;